import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { ButtonGroup, ButtonLink } from "../../../components/Buttons";
import Card from "../../../components/Card";
import { Dd, Dl, Dt } from "../../../components/DescriptionList/Horizontal";
import Grid, { GridItem } from "../../../components/Grid";
import Hr from "../../../components/Hr";
import Icon from "../../../components/Icons";
import { LoadingOverlay } from "../../../components/Loading";
import { PageButton } from "../../../components/Page";
import { H2 } from "../../../components/Typography";
import View from "../../../components/View";
import { defaultRadius, palette } from "../../../styles/constants";
import type { SalesLocation } from "../../../types/customers";
import type { CreateBankAccountTempate } from "../../components/PayoutBankAccount/CreateBankAccountDialog";
import {
    type SalesLocationQuery,
    useLocations,
} from "../../locations/hooks/useLocations";
import { getGeoLocation, mapValues } from "../../locations/shared/utils";
import type { LocationFormValues } from "../../locations/types";
import SellerLocationForm from "./SellerLocationForm";

export type SellerLocationsProps = {
    organizationNumber?: string;
    accountId?: string;
    disabled?: boolean;
    template?: CreateBankAccountTempate;
};

type LocationsGridProps = {
    locations: SalesLocation[];
    isLoading: boolean;
    accountId?: string;
};

const LocationsGrid = ({
    locations,
    isLoading,
    accountId,
}: LocationsGridProps) => {
    const { t } = useTranslation("app");

    if (!locations || isLoading) {
        return <LoadingOverlay />;
    }

    const locationsTiedToSeller = Object.values(locations)
        .flat()
        .filter((l) => (accountId ? l.account_id === accountId : l));

    return (
        <>
            <Grid gap={24} alignItems="stretch">
                {locationsTiedToSeller.map((location) => (
                    <GridItem key={location.id}>
                        <OutlinedWrapper>
                            <div>
                                <Dl break={1000}>
                                    <Dt span="span2">
                                        {t(
                                            "locations.location_form.fields.environment",
                                        )}
                                    </Dt>
                                    <Dd>
                                        {location.account_id?.startsWith("T")
                                            ? t("environments.test")
                                            : t("environments.production")}
                                    </Dd>
                                </Dl>
                                {location.name && (
                                    <Dl break={1000}>
                                        <Dt span="span2">
                                            {t(
                                                "locations.location_form.fields.name",
                                            )}
                                        </Dt>
                                        <Dd>{location.name}</Dd>
                                    </Dl>
                                )}
                                <Dl break={1000}>
                                    <Dt span="span2">
                                        {t(
                                            "locations.location_form.fields.location_id",
                                        )}
                                    </Dt>
                                    <Dd>{location.location_id}</Dd>
                                </Dl>
                                {location.address &&
                                    location.address.country && (
                                        <Dl break={1000}>
                                            <Dt span="span2">
                                                {t(
                                                    "locations.location_form.fields.country",
                                                )}
                                            </Dt>
                                            <Dd>{location.address?.country}</Dd>
                                        </Dl>
                                    )}
                                {location.address &&
                                    location.address.address_line && (
                                        <Dl break={1000}>
                                            <Dt span="span2">
                                                {t(
                                                    "locations.locations_list.fields.address",
                                                )}
                                            </Dt>
                                            <Dd>
                                                {location.address?.address_line}
                                            </Dd>
                                        </Dl>
                                    )}
                                {location.email && (
                                    <Dl break={1000}>
                                        <Dt span="span2">
                                            {t(
                                                "locations.locations_list.fields.contact",
                                            )}
                                        </Dt>
                                        <Dd>{location.email}</Dd>
                                    </Dl>
                                )}
                            </div>
                            <ButtonGroup mt="var(--Spacing-1)" mb={8}>
                                <ButtonLink
                                    to={`/${location.account_id}/settings/locations/${location.location_id}`}
                                    className="alt"
                                >
                                    {t("locations.locations_list.view")}
                                </ButtonLink>
                            </ButtonGroup>
                        </OutlinedWrapper>
                    </GridItem>
                ))}
            </Grid>
        </>
    );
};

export const SellerLocations = ({
    organizationNumber,
    accountId,
    disabled,
    template,
}: SellerLocationsProps) => {
    const [addingNewLocation, setAddingNewLocation] = useState(false);
    const [params, setParams] = useState<SalesLocationQuery>({
        limit: 100,
    });
    const { locations, createLocation, isLoading } = useLocations(params);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (organizationNumber) {
            setParams((prev) => ({
                ...prev,
                organization_number: [organizationNumber],
            }));
        }
    }, [organizationNumber]);

    const onConfirmAddingNewLocation = async (values: LocationFormValues) => {
        const location = mapValues(values);
        const geoLocation = await getGeoLocation(
            location.address!,
            i18n.language,
        );

        await createLocation({
            location: {
                ...location,
                address: {
                    ...location.address!,
                    ...(geoLocation || {}),
                },
            },
            accountId: values.accountId,
        });
    };

    return (
        <>
            <Card>
                <View justify="space-between">
                    <H2>{t("payout:payout_destination.locations")}</H2>
                    <PageButton
                        disabled={!organizationNumber || disabled}
                        onClick={() => setAddingNewLocation(true)}
                    >
                        <Icon icon="plus" />
                        {t("payout:payout_destination.add_a_location")}
                    </PageButton>
                </View>
                <Hr />
                {organizationNumber ? (
                    <LocationsGrid
                        locations={locations}
                        accountId={accountId}
                        isLoading={isLoading}
                    />
                ) : (
                    <span>
                        {t(
                            "payout:payout_destination.no_bank_account_location",
                        )}
                    </span>
                )}
            </Card>
            <SellerLocationForm
                organizationNumber={template?.owner_orgno}
                asDialog={{
                    onClose: () => setAddingNewLocation(false),
                    open: addingNewLocation,
                    cancelText: t(
                        "payout:payout_destination.location_form.cancel",
                    ),
                    saveText: t("payout:payout_destination.location_form.save"),
                    title: t("payout:payout_destination.add_a_location"),
                }}
                onSubmit={onConfirmAddingNewLocation}
                autoSave={false}
            />
        </>
    );
};

const OutlinedWrapper = styled.div`
    padding-inline: 16px;
    padding-block: 8px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    border: 1px solid ${palette.neutral[200]};
    border-radius: ${defaultRadius};
`;
